/** Parse film/series detail Qs: filmmaker, status, purchases, etc. (pure). */

export type SlateDetailFocus =
    | 'filmmaker'
    | 'status'
    | 'purchases'
    | 'views'
    | 'revenue'
    | 'price'
    | 'summary';

export interface SlateFilmLookupCommand {
    title: string;
    focuses: Exclude<SlateDetailFocus, 'summary'>[] | ['summary'];
    kind: 'film' | 'webseries' | 'unknown';
}

const MONTH =
    'jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?';

/** Dated metrics belong to analytics — never steal as title lookup. */
export function hasAnalyticsDateCue(text: string): boolean {
    const t = text.toLowerCase();
    if (
        /\blast\s+\d+\s+days?\b|\blast\s+(?:week|month|year)\b|\bthis\s+(?:week|month|year)\b|\bprevious\s+(?:week|month|year)\b|\bbetween\b|\bfrom\s+\d|\byesterday\b|\btoday\b/.test(
            t
        )
    ) {
        return true;
    }
    // "7th August 2026", "on 10 Aug", "August 10 2026"
    if (
        new RegExp(
            `\\b\\d{1,2}(?:st|nd|rd|th)?\\s+(?:${MONTH})(?:\\s*,?\\s*\\d{4})?\\b`
        ).test(t)
    ) {
        return true;
    }
    if (
        new RegExp(
            `\\b(?:${MONTH})\\s+\\d{1,2}(?:st|nd|rd|th)?(?:\\s*,?\\s*\\d{4})?\\b`
        ).test(t)
    ) {
        return true;
    }
    if (new RegExp(`\\b(?:${MONTH})\\s+\\d{4}\\b`).test(t)) {
        return true;
    }
    if (/\b\d{1,2}[\/.\-]\d{1,2}[\/.\-]\d{2,4}\b/.test(t)) {
        return true;
    }
    if (/\bon\s+\d{1,2}(?:st|nd|rd|th)?\b/.test(t)) {
        return true;
    }
    return false;
}

export function looksLikeDatePhrase(s: string): boolean {
    const t = s.toLowerCase().trim().replace(/^on\s+/, '');
    if (!t) return true;
    if (hasAnalyticsDateCue(t)) return true;
    if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return true;
    if (/^(last|this|previous)\s/.test(t)) return true;
    return false;
}

export function isFilmLookupCommand(text: string): boolean {
    const t = text.toLowerCase().trim();
    if (!t) return false;
    // Do not steal how-to / navigation
    if (
        /how\s+(?:do\s+i|to|can\s+i)|where\s+(?:is|can|do)|steps?\s+to|guide\s+me/.test(
            t
        )
    ) {
        return false;
    }
    // Date-ranged metrics belong to analytics / reports
    if (hasAnalyticsDateCue(t)) {
        return false;
    }
    // Phone/email user lookup handled elsewhere
    if (
        /(?:\+|00)?\s*\d{1,3}[\s-]?\d{6,12}|\b\d{10,15}\b/.test(t) ||
        /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i.test(t)
    ) {
        return false;
    }
    // "Who is Prashant?" is user lookup — not content
    if (
        /\bwho\s+(?:is|are|was)\b/.test(t) &&
        !/\b(?:film\s*makers?|filmmakers?|maker|producer|director|uploaded|made|of|for)\b/.test(
            t
        )
    ) {
        return false;
    }
    if (
        /\bwho\s+(?:is|are|was)\b/.test(t) &&
        /\b(?:film\s*makers?|filmmakers?|maker|producer|director|uploaded|made)\b/.test(
            t
        )
    ) {
        return true;
    }
    if (
        /\b(?:film\s*makers?|filmmakers?)\s+of\b/.test(t) ||
        /\bwho\s+made\b/.test(t) ||
        /\bwho\s+uploaded\b/.test(t)
    ) {
        return true;
    }
    if (
        /\b(?:what(?:'s| is| are)?|show|give|get|tell)\b/.test(t) &&
        /\b(?:status|purchases?|sales|views?|revenue|earnings|price|details?|info)\b/.test(
            t
        )
    ) {
        return true;
    }
    if (
        /\b(?:status|purchases?|sales|views?|revenue|earnings|price)\s+(?:of|for|on)\b/.test(
            t
        )
    ) {
        return true;
    }
    // "how many views" alone / with date already gated — only with clear title cue
    if (/\bhow\s+many\s+(?:purchases?|sales|views?|tickets?)\b/.test(t)) {
        return (
            /["']/.test(text) ||
            /\b(?:film|movie|web\s*series|series)\b/.test(t) ||
            /\b(?:of|for)\s+(?!last\b|this\b|the\s+last\b)/.test(t)
        );
    }
    if (
        /\b(?:tell\s+me\s+about|details?\s+(?:of|for|on)|info\s+(?:on|about|for))\b/.test(
            t
        )
    ) {
        return true;
    }
    return false;
}

export function extractFilmLookup(
    text: string
): SlateFilmLookupCommand | null {
    if (!isFilmLookupCommand(text)) return null;
    const focuses = detectFocuses(text);
    const title = extractLookupTitle(text);
    if (!title || looksLikeDatePhrase(title)) return null;
    return { title, focuses, kind: detectContentKind(text) };
}

function detectContentKind(text: string): 'film' | 'webseries' | 'unknown' {
    const t = text.toLowerCase();
    if (
        /\bweb\s*series\b/.test(t) ||
        (/\bseries\b/.test(t) && !/\bfilm\b/.test(t) && !/\bmovie\b/.test(t))
    ) {
        return 'webseries';
    }
    // "film maker" is a person role, not content kind
    const withoutMaker = t
        .replace(/\bfilm\s*makers?\b/g, ' ')
        .replace(/\bfilmmakers?\b/g, ' ');
    if (/\b(film|movie)\b/.test(withoutMaker)) return 'film';
    return 'unknown';
}

function detectFocuses(
    text: string
): Exclude<SlateDetailFocus, 'summary'>[] | ['summary'] {
    const t = text.toLowerCase();
    const found: Exclude<SlateDetailFocus, 'summary'>[] = [];

    if (
        /\b(?:film\s*makers?|filmmakers?|maker|producer|director|who\s+made|who\s+uploaded|who\s+is)\b/.test(
            t
        ) &&
        !/\bhow\s+many\b/.test(t)
    ) {
        // "who is" alone without maker words already gated; still add filmmaker when makerish
        if (
            /\b(?:film\s*makers?|filmmakers?|maker|producer|director|who\s+made|who\s+uploaded)\b/.test(
                t
            ) ||
            (/\bwho\s+(?:is|are|was)\b/.test(t) &&
                /\b(?:of|for)\b/.test(t))
        ) {
            found.push('filmmaker');
        }
    }
    if (/\bstatus\b/.test(t)) found.push('status');
    if (/\bpurchases?\b|\bsales\b|\btickets?\s+sold\b|\bhow\s+many\s+purchases?\b/.test(t)) {
        found.push('purchases');
    }
    if (/\bviews?\b/.test(t) && !/\bview\s+mode\b/.test(t)) {
        found.push('views');
    }
    if (/\brevenue\b|\bearnings?\b/.test(t)) found.push('revenue');
    if (/\bprice\b|\bcost\b|\bticket\s+price\b/.test(t)) found.push('price');

    if (
        /\b(?:details?|info|tell\s+me\s+about|everything|all\s+(?:about|details))\b/.test(
            t
        ) &&
        found.length === 0
    ) {
        return ['summary'];
    }

    if (found.length === 0) return ['summary'];
    // de-dupe
    return [...new Set(found)] as Exclude<SlateDetailFocus, 'summary'>[];
}

function extractLookupTitle(raw: string): string | undefined {
    const quoted = raw.match(/["']([^"']{1,120})["']/);
    if (quoted?.[1]) return cleanLookupTitle(quoted[1]);

    let m = raw.match(
        /\b(?:film\s*makers?|filmmakers?|maker|producer|director)\s+(?:of|for)\s+(.+?)(?:\?|$)/i
    );
    if (m?.[1]) return cleanLookupTitle(m[1]);

    m = raw.match(
        /\bwho\s+(?:is|are|was)\s+(?:the\s+)?(?:film\s*makers?|filmmakers?|maker|producer|director)\s+(?:of|for)\s+(.+?)(?:\?|$)/i
    );
    if (m?.[1]) return cleanLookupTitle(m[1]);

    m = raw.match(
        /\bwho\s+(?:made|uploaded|produced)\s+(.+?)(?:\?|$)/i
    );
    if (m?.[1]) return cleanLookupTitle(m[1]);

    // Prefer of/for over on (on often introduces dates)
    m = raw.match(
        /\b(?:status|purchases?|sales|views?|revenue|earnings|price)\s+(?:of|for)\s+(.+?)(?:\?|$)/i
    );
    if (m?.[1]) return cleanLookupTitle(m[1]);

    m = raw.match(
        /\b(?:status|purchases?|sales|views?|revenue|earnings|price)\s+on\s+(.+?)(?:\?|$)/i
    );
    if (m?.[1] && !looksLikeDatePhrase(m[1])) {
        return cleanLookupTitle(m[1]);
    }

    m = raw.match(
        /\bhow\s+many\s+(?:purchases?|sales|views?|tickets?)\s+(?:does\s+|did\s+)?(?:(?:the\s+)?(?:film|movie|series)\s+)?(.+?)(?:\s+have)?(?:\?|$)/i
    );
    if (m?.[1]) {
        let rest = m[1].trim().replace(/^(?:of|for|on)\s+/i, '');
        if (looksLikeDatePhrase(rest) || /^(?:on\s+)/i.test(m[1])) {
            return undefined;
        }
        return cleanLookupTitle(rest);
    }

    m = raw.match(
        /\b(?:tell\s+me\s+about|details?\s+(?:of|for|on)|info\s+(?:on|about|for))\s+(.+?)(?:\?|$)/i
    );
    if (m?.[1]) return cleanLookupTitle(m[1]);

    m = raw.match(
        /\b(?:what(?:'s| is| are)?|show|give|get)\s+(?:the\s+)?(?:status|purchases?|sales|views?|revenue|price)\s+(?:of|for|on)\s+(.+?)(?:\?|$)/i
    );
    if (m?.[1]) return cleanLookupTitle(m[1]);

    m = raw.match(
        /\b(?:film|movie)\s+["']?([^"'?]+?)["']?\s*(?:\?|$)/i
    );
    if (
        m?.[1] &&
        /\b(status|purchase|sale|view|revenue|maker|who)\b/i.test(raw)
    ) {
        return cleanLookupTitle(m[1]);
    }

    return undefined;
}

function cleanLookupTitle(s: string): string | undefined {
    let cleaned = s
        .replace(/["']/g, '')
        .replace(/^(?:please|the|a|an)\s+/i, '')
        .replace(/\s+/g, ' ')
        .trim();
    cleaned = cleaned
        .replace(
            /^(?:film|movie|web\s*series|series)\s+/i,
            ''
        )
        .trim();
    cleaned = cleaned
        .replace(
            /\s+(?:film|movie|web\s*series|series)$/i,
            ''
        )
        .trim();
    // Strip trailing date clauses: "Inception on 7th August 2026"
    cleaned = cleaned
        .replace(
            new RegExp(
                `\\s+(?:on|for|dated)\\s+\\d{1,2}(?:st|nd|rd|th)?\\s+(?:${MONTH})(?:\\s*,?\\s*\\d{4})?$`,
                'i'
            ),
            ''
        )
        .trim();
    cleaned = cleaned.replace(/\?+$/, '').trim();
    if (cleaned.length < 1) return undefined;
    if (looksLikeDatePhrase(cleaned)) return undefined;
    if (
        /^(last|this|previous|today|yesterday|from|between|top|how|where|status|purchases?|sales|views?|you|me)\b/i.test(
            cleaned
        ) ||
        /^(last|this|previous)\s+\d+\s+days?$/i.test(cleaned) ||
        /^(last|this|previous)\s+(?:week|month|year)$/i.test(cleaned)
    ) {
        return undefined;
    }
    return cleaned;
}
